import { all, call, put, select } from "redux-saga/effects";
import { actions } from "../../actions";
import { updateUserPassenger } from "../../../../api/v0/book/user-passengers/updateUserPassenger";
import {
  fetchUserPassengers,
  IListUserPassengersResponse,
} from "../../../../api/v0/book/user-passengers/fetchUserPassengers";
import {
  ADD_DRIVERS_LICENSE,
  ADD_TRAVELER,
  CallState,
  CorpSessionInfo,
  ICorpPerson,
  IPerson,
  SessionInfo,
} from "redmond";
import { getUserPassengers } from "../../reducer";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { useCorpPersonData } from "@capone/common/src/hooks/useCorpPersonData";
import updateUserDefaultTraveler from "b2b-base/src/api/v1/user/updateUserDefaultTraveler";
import fetchUserInfo from "../../../../api/v0/user/fetchUserInfo";
import { isCorpTenant } from "@capone/common";
import { config } from "../../../../api/config";
import fetchCorpUsersList, {
  CorpUsersListResponse,
} from "b2b-base/src/api/v1/user/fetchCorpUsersList";
import { buildCorpTravelers } from "b2b-base/src/utils/capone-corporate/travelersMappingUtils";

export function* updateUserPassengerSaga(action: actions.IUpdateUserPassenger) {
  try {
    // Check if passport field is valid or filter it
    // & filter driverLicense field
    // ^ Adding back driverLicense field as part of BOPS-628
    const { person } = action.payload;
    const { passport, ...restOfPayload } = person;
    const personToUpdate: IPerson = { ...restOfPayload };
    if (passport?.countryOfIssue?.length === 2) {
      personToUpdate.passport = passport;
    }
    yield updateUserPassenger({ person: personToUpdate });
    const currentTravelers: IPerson[] = yield select(getUserPassengers);

    const [shouldAssignTraveler, deUserId] = useCorpPersonData(
      "isGhostUser" in person ? (person as ICorpPerson) : null
    );

    if (shouldAssignTraveler) {
      yield updateUserDefaultTraveler({
        travelerId: personToUpdate.id,
        userId: deUserId,
      });
    }

    const sessionInfo: SessionInfo = yield call(fetchUserInfo);
    const canViewUsers =
      "corporateInfo" in sessionInfo
        ? (sessionInfo as CorpSessionInfo).corporateInfo.permissions
            .canViewUsers
        : false;

    const travelerDefaultTravelerId =
      ("corporateInfo" in sessionInfo &&
        (sessionInfo as CorpSessionInfo).corporateInfo.defaultTravelerId) ??
      "";

    let userPassengers: IPerson[] | ICorpPerson[];
    if (!isCorpTenant(config.TENANT)) {
      userPassengers = yield fetchUserPassengers({});
    } else {
      const [corpUsersList, caponeUserPassengers]: [
        CorpUsersListResponse,
        IListUserPassengersResponse
      ] = yield all([
        canViewUsers
          ? call(fetchCorpUsersList)
          : {
              users: [
                {
                  id: sessionInfo.userId,
                  firstName: sessionInfo.userInfo.firstName,
                  lastName: sessionInfo.userInfo.lastName,
                  defaultTravelerId: travelerDefaultTravelerId,
                },
              ],
            },
        call(fetchUserPassengers, {}),
      ]);

      const hiddenTravelerIds =
        "corporateInfo" in sessionInfo
          ? (sessionInfo as CorpSessionInfo).corporateInfo.hiddenTravelerIds
          : [];

      const loggedInUserId = sessionInfo.userId;

      userPassengers = buildCorpTravelers(
        caponeUserPassengers,
        corpUsersList.users,
        hiddenTravelerIds,
        loggedInUserId
      );
    }

    if (currentTravelers.length < userPassengers.length) {
      trackEvent({
        eventName: ADD_TRAVELER,
        properties: {},
      });
      yield put(
        actions.setUserSelectedPassengerIds({
          userSelectedPassengerIds: [person?.id],
        })
      );
    }

    if (action.addDriversLicense) {
      trackEvent({
        eventName: ADD_DRIVERS_LICENSE,
        properties: {},
      });
    }

    yield put(
      actions.setUserPassengers({
        userPassengerCallState: CallState.Success,
        userPassengers: userPassengers,
      })
    );
  } catch (e) {
    yield put(actions.setUserPassengersCallStateFailed());
  }
}
